export default function mapController(initialWidth = 4500, mobileWidth = 2000) {
	return {
		x: 0, // Current X position
		y: 0, // Current Y position
		startX: 0, // Starting X position
		startY: 0, // Starting Y position
		isMapHovered: false, // State for map hover cursor
		isRiveHovered: false, // State for Rive hover cursor
		width: initialWidth,
		initialWidth: initialWidth,
		mobileWidth: mobileWidth,
		containerWidth: window.innerWidth,
		containerHeight: window.innerHeight,
		mapHeight: 0,
		lastDragTime: 0, // For debouncing drag events
		dragThreshold: 16, // Milliseconds between drag updates
		isMobile: window.innerWidth < 767,

		init() {
			// Use debounced resize handler
			window.addEventListener("resize", this.debounce(this.handleResize.bind(this), 100));
		},

		// Set width based on viewport size
		setResponsiveWidth() {
			this.width = window.innerWidth < 767 ? this.mobileWidth : this.initialWidth;
		},

		// Handle responsive canvas elements
		handleResponsiveCanvases() {
			const canvases = document.querySelectorAll('canvas[data-responsive="true"]');

			canvases.forEach(canvas => {
				// Apply initial sizing based on viewport
				this.applyCanvasResponsiveSizing(canvas);
			});
		},

		// Apply responsive sizing to a canvas element
		applyCanvasResponsiveSizing(canvas) {
			const isMobile = window.innerWidth < 767;
			const desktopSize = canvas.getAttribute('data-desktop-size');
			const mobileSize = canvas.getAttribute('data-mobile-size');

			if (isMobile) {
				// On mobile: swap width and height
				canvas.style.width = `${mobileSize}px`;
				canvas.style.height = `${mobileSize}px`;
			} else {
				// On desktop: normal sizing
				canvas.style.width = `${desktopSize}px`;
				canvas.style.height = `${desktopSize}px`;
			}
		},

		// Debounce helper function
		debounce(func, wait) {
			let timeout;
			return function (...args) {
				const context = this;
				clearTimeout(timeout);
				timeout = setTimeout(() => func.apply(context, args), wait);
			};
		},

		// Throttle helper function for drag operations
		throttle(func, limit) {
			return function (...args) {
				const now = Date.now();
				if (now - this.lastDragTime >= limit) {
					func.apply(this, args);
					this.lastDragTime = now;
				}
			}.bind(this);
		},

		setupMap() {
			this.$nextTick(() => {
				this.setResponsiveWidth();
				this.handleResponsiveCanvases();
				const map = this.$refs.map;

				// Wait for the map image to fully load before calculating dimensions
				const mapImage = map.querySelector('img');
				const calculateDimensions = () => {
					this.mapHeight = map.offsetHeight;
					this.applyBoundaries();
					this.centerMap();
				};

				if (mapImage && !mapImage.complete) {
					// If image is not yet loaded, wait for it
					mapImage.addEventListener('load', calculateDimensions);
				} else {
					// Image already loaded or no image found, use timeout as fallback
					setTimeout(calculateDimensions, 250);
				}

				// Use debounced resize handler
				window.addEventListener("resize", this.debounce(this.handleResize.bind(this), 100));
			});
		},

		centerMap() {
			// Calculate center position
			const scaledWidth = this.width;
			const scaledHeight = this.mapHeight;
			this.x = -(scaledWidth - this.containerWidth) / 2;
			this.y = -(scaledHeight - this.containerHeight) / 2;

			// Use requestAnimationFrame for smoother updates
			requestAnimationFrame(() => this.applyBoundaries());
		},

		handleResize() {
			this.containerWidth = window.innerWidth;
			this.containerHeight = window.innerHeight;
			this.isMobile = window.innerWidth < 767;

			// Update width based on new viewport size
			this.setResponsiveWidth();

			const map = this.$refs.map;
			const mapImage = map.querySelector('img');
			const recalculateMapBounds = () => {
				this.mapHeight = map.offsetHeight;
				this.applyBoundaries();
				this.centerMap();
			};

			if (mapImage && !mapImage.complete) {
				mapImage.addEventListener('load', recalculateMapBounds);
			} else {
				setTimeout(recalculateMapBounds, 250);
			}

			// Update all responsive canvases
			document.querySelectorAll('canvas[data-responsive="true"]').forEach(canvas => {
				this.applyCanvasResponsiveSizing(canvas);
			});
		},

		startDrag(event) {
			const pos = this.getEventPosition(event);
			this.startX = pos.x - this.x;
			this.startY = pos.y - this.y;
			this.lastDragTime = Date.now(); // Initialize the throttle timer
			this.$store.map.isDragging = true;
		},

		endDrag() {
			this.$store.map.isDragging = false;

			// Delay resetting hasDraggedAboveThreshold
			setTimeout(() => {
				this.$store.map.hasDraggedAboveThreshold = false;
			}, 250);
		},

		drag(event) {
			if (!this.$store.map.isDragging) return;

			const pos = this.getEventPosition(event);
			const deltaX = Math.abs(pos.x - (this.startX + this.x));
			const deltaY = Math.abs(pos.y - (this.startY + this.y));

			// Check if drag exceeds threshold
			//
			const dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
			if (dragDistance > 10) {
				this.$store.map.hasDraggedAboveThreshold = true;
			}

			this.x = pos.x - this.startX;
			this.y = pos.y - this.startY;

			// Use requestAnimationFrame for smoother updates
			requestAnimationFrame(() => this.applyBoundaries());
		},

		applyBoundaries() {
			const scaledWidth = this.width;
			const scaledHeight = this.mapHeight;

			// If scaled size is smaller than container, center it
			if (scaledWidth <= this.containerWidth) {
				this.x = (this.containerWidth - scaledWidth) / 2;
			} else {
				// Otherwise, apply boundaries
				const minX = -(scaledWidth - this.containerWidth);
				this.x = Math.min(0, Math.max(minX, this.x));
			}

			if (scaledHeight <= this.containerHeight) {
				this.y = (this.containerHeight - scaledHeight) / 2;
			} else {
				const minY = -(scaledHeight - this.containerHeight);
				this.y = Math.min(0, Math.max(minY, this.y));
			}
		},

		getEventPosition(event) {
			if (event.touches) {
				return { x: event.touches[0].clientX, y: event.touches[0].clientY };
			}
			return { x: event.clientX, y: event.clientY };
		},

		hoverRive(isHovered) {
			this.isRiveHovered = isHovered;
		}
	};
}
